import { Inject, Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { CheckoutFacade, CheckoutService } from '@checkout/data-access';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  CheckoutDirectDebitMethod,
  CmsDirectDebitPage,
  DirectDebitForm,
  DirectDebitFormField,
  DirectDebitFormValue,
} from '@checkout/util-models';
import {
  AuthFacade,
  UserDirectDebitDetails,
} from '@domgen/dgx-fe-authentication';
import { BasketFacade, QuoteDetails } from '@domgen/dgx-fe-basket';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { ProductType, filterNullUndefined } from '@domgen/dgx-fe-common';
import {
  FORM_CONFIG,
  FormConfig,
  FormOption,
  disableFormControls,
  enableFormControls,
  pluckControl,
} from '@domgen/dgx-fe-forms/shared';
import { GAEvent, GAService, extractPageNameFromUrl } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, of } from 'rxjs';
import {
  filter,
  finalize,
  first,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { directDebitPaymentDateOptions } from '../../constants/direct-debit-payment-date-options.constant';
import { DirectDebitFormConfig } from '../../models/direct-debit-form/direct-debit-form-config.interface';
import { DirectDebitFormBuilder } from '../direct-debit-form-builder/direct-debit-form-builder.service';

export interface ViewModel {
  brandCodes: string[];
  cmsData: CmsDirectDebitPage;
  directDebitMethod?: CheckoutDirectDebitMethod;
  directDebitPaymentDateOptions: FormOption<number>[];
  editingDirectDebitDetails: boolean;
  form: FormGroup<DirectDebitForm>;
  legalProvider: string;
  numberWorkingDays?: number;
  purchasing: boolean;
  selectedQuotes: QuoteDetails[];
  showExistingUserDirectDebitDetails: boolean;
  showRequiredFieldLegend: boolean;
  userDirectDebitDetails?: UserDirectDebitDetails;
  validatingAccountDetails: boolean;
}

export interface ComponentState {
  brandCodes: string[];
  cmsData: CmsDirectDebitPage;
  directDebitMethod?: CheckoutDirectDebitMethod;
  directDebitPaymentDateOptions: FormOption<number>[];
  editingDirectDebitDetails: boolean;
  form?: FormGroup<DirectDebitForm>;
  numberWorkingDays?: number;
  purchasing: boolean;
  showExistingUserDirectDebitDetails: boolean;
  userDirectDebitDetails?: UserDirectDebitDetails;
  validatingAccountDetails: boolean;
}

@Injectable()
export class DirectDebitFormComponentService extends ComponentStore<ComponentState> {
  private readonly brandCodes$: Observable<string[]> = this.select(
    (state: ComponentState) => state.brandCodes
  );

  private readonly cmsData$: Observable<CmsDirectDebitPage> = this.select(
    (state: ComponentState) => state.cmsData
  );

  private readonly directDebitMethod$: Observable<CheckoutDirectDebitMethod> =
    this.select((state: ComponentState) => state.directDebitMethod).pipe(
      filterNullUndefined()
    );

  private readonly directDebitPaymentDateOptions$: Observable<
    FormOption<number>[]
  > = this.select(
    (state: ComponentState) => state.directDebitPaymentDateOptions
  );

  private readonly editingDirectDebitDetails$: Observable<boolean> =
    this.select((state: ComponentState) => state.editingDirectDebitDetails);

  private readonly form$: Observable<FormGroup<DirectDebitForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  private readonly numberWorkingDays$: Observable<number | undefined> =
    this.select((state: ComponentState) => state.numberWorkingDays);

  private readonly purchasing$: Observable<boolean> = this.select(
    (state: ComponentState) => state.purchasing
  );

  private readonly showExistingUserDirectDebitDetails$: Observable<boolean> =
    this.select(
      (state: ComponentState) => state.showExistingUserDirectDebitDetails
    );

  private readonly userDirectDebitDetails$: Observable<
    UserDirectDebitDetails | undefined
  > = this.select((state: ComponentState) => state.userDirectDebitDetails);

  private readonly validatingAccountDetails$: Observable<boolean> = this.select(
    (state: ComponentState) => state.validatingAccountDetails
  );

  private readonly formConfig$: Observable<DirectDebitFormConfig> =
    combineLatest({
      method: this.directDebitMethod$,
      termsAndConditionsFeatureEnabled: of(
        this.checkoutConfig.features.checkoutTermsAndConditions
      ),
    });

  readonly vm$: Observable<ViewModel> = combineLatest({
    brandCodes: this.brandCodes$,
    cmsData: this.cmsData$,
    directDebitMethod: this.directDebitMethod$,
    directDebitPaymentDateOptions: this.directDebitPaymentDateOptions$,
    editingDirectDebitDetails: this.editingDirectDebitDetails$,
    form: this.form$,
    legalProvider: this.basketFacade.legalProvider$,
    numberWorkingDays: this.numberWorkingDays$,
    purchasing: this.purchasing$,
    selectedQuotes: this.basketFacade.selectedQuotes$,
    showExistingUserDirectDebitDetails:
      this.showExistingUserDirectDebitDetails$,
    showRequiredFieldLegend: of(Boolean(!this.formConfig.requiredHidden)),
    userDirectDebitDetails: this.userDirectDebitDetails$,
    validatingAccountDetails: this.validatingAccountDetails$,
  });

  constructor(
    @Inject(CHECKOUT_CONFIG) readonly checkoutConfig: CheckoutConfig,
    @Inject(FORM_CONFIG) private readonly formConfig: FormConfig,
    private readonly authFacade: AuthFacade,
    private readonly basketFacade: BasketFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly checkoutService: CheckoutService,
    private readonly cmsContainer: CmsContainer<CmsDirectDebitPage>,
    private readonly directDebitFormBuilder: DirectDebitFormBuilder,
    private readonly gaService: GAService
  ) {
    super({
      brandCodes: [],
      cmsData: cmsContainer.content,
      directDebitMethod: checkoutConfig.directDebit?.method,
      directDebitPaymentDateOptions,
      editingDirectDebitDetails: false,
      numberWorkingDays: 10,
      purchasing: false,
      showExistingUserDirectDebitDetails: false,
      validatingAccountDetails: false,
    });
  }

  private readonly updateBrandCodes = this.updater(
    (state: ComponentState, brandCodes: string[]) => ({
      ...state,
      brandCodes,
    })
  )(
    this.basketFacade.selectedQuotes$.pipe(
      map((selectedQuotes: QuoteDetails[]) =>
        selectedQuotes.map(
          (selectedQuote: QuoteDetails) => selectedQuote.brandCode
        )
      )
    )
  );

  readonly updateEditingDirectDebitDetails = this.updater(
    (state: ComponentState, editingDirectDebitDetails: boolean) => ({
      ...state,
      editingDirectDebitDetails,
    })
  );

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<DirectDebitForm>) => ({
      ...state,
      form,
    })
  );

  private readonly updateNumberWorkingDays = this.updater(
    (state: ComponentState, numberWorkingDays?: number) => ({
      ...state,
      numberWorkingDays,
    })
  )(
    this.basketFacade
      .hasSelectedProductType(ProductType.BoilerCare)
      .pipe(
        map((hasSelectedBoilerCareProduct: boolean) =>
          hasSelectedBoilerCareProduct
            ? this.checkoutConfig.boilerCareNumberOfWorkingDaysNotification
            : this.checkoutConfig.applianceCareNumberOfWorkingDaysNotification
        )
      )
  );

  private readonly updatePurchasing = this.updater(
    (state: ComponentState, purchasing: boolean) => ({
      ...state,
      purchasing,
    })
  )(this.checkoutFacade.purchasing$);

  private readonly updateshowExistingUserDirectDebitDetails = this.updater(
    (state: ComponentState, showExistingUserDirectDebitDetails: boolean) => ({
      ...state,
      showExistingUserDirectDebitDetails: showExistingUserDirectDebitDetails,
    })
  )(
    combineLatest([
      this.userDirectDebitDetails$,
      this.editingDirectDebitDetails$,
    ]).pipe(
      map(
        ([userDirectDebitDetails, editingDirectDebitDetails]) =>
          Boolean(userDirectDebitDetails) && !editingDirectDebitDetails
      )
    )
  );

  private readonly updateUserDirectDebitDetails = this.updater(
    (
      state: ComponentState,
      userDirectDebitDetails: UserDirectDebitDetails
    ) => ({
      ...state,
      userDirectDebitDetails,
    })
  )(this.authFacade.userDirectDebitDetails$.pipe(filterNullUndefined()));

  private readonly updateValidatingAccountDetails = this.updater(
    (state: ComponentState, validatingAccountDetails: boolean) => ({
      ...state,
      validatingAccountDetails,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: DirectDebitFormConfig) =>
        this.directDebitFormBuilder.buildDirectDebitForm(config)
      ),
      tap((form) => this.updateForm(form))
    )
  );

  private readonly setPreferredPaymentDate = this.effect(
    (trigger$: Observable<[AbstractControl, UserDirectDebitDetails]>) =>
      trigger$.pipe(
        tap(([control, userDirectDebitDetails]) =>
          control.setValue(userDirectDebitDetails.bankDetails.nominatedPerDay)
        )
      )
  )(
    combineLatest([
      this.form$.pipe(pluckControl(DirectDebitFormField.PaymentDate)),
      this.userDirectDebitDetails$.pipe(filterNullUndefined()),
    ])
  );

  private readonly setPaymentToken = this.effect(
    (trigger$: Observable<[AbstractControl, boolean]>) =>
      trigger$.pipe(
        withLatestFrom(this.userDirectDebitDetails$),
        tap(([[control, editingDirectDebitDetails], userDirectDebitDetails]) =>
          control.setValue(
            editingDirectDebitDetails
              ? null
              : userDirectDebitDetails?.paymentToken || null
          )
        )
      )
  )(
    combineLatest([
      this.form$.pipe(pluckControl(DirectDebitFormField.PaymentToken)),
      this.editingDirectDebitDetails$,
    ])
  );

  private readonly toggleEditingDirectDebitDetailsFormFields = this.effect(
    (
      trigger$: Observable<
        [
          FormGroup<DirectDebitForm>,
          boolean,
          UserDirectDebitDetails | undefined
        ]
      >
    ) =>
      trigger$.pipe(
        filter(([, , userDirectDebitDetails]) =>
          Boolean(userDirectDebitDetails)
        ),
        tap(([form, editingDirectDebitDetails]) => {
          if (editingDirectDebitDetails) {
            enableFormControls(
              form.get(DirectDebitFormField.AccountName),
              form.get(DirectDebitFormField.AccountNumber),
              form.get(DirectDebitFormField.SortCode)
            );
            disableFormControls(form.get(DirectDebitFormField.PaymentToken));
          } else {
            disableFormControls(
              form.get(DirectDebitFormField.AccountName),
              form.get(DirectDebitFormField.AccountNumber),
              form.get(DirectDebitFormField.SortCode)
            );
            enableFormControls(form.get(DirectDebitFormField.PaymentToken));
          }
        })
      )
  )(
    combineLatest([
      this.form$,
      this.editingDirectDebitDetails$,
      this.userDirectDebitDetails$,
    ])
  );

  validateAccountDetails(
    formValue: DirectDebitFormValue
  ): Observable<DirectDebitFormValue | undefined> {
    this.updateValidatingAccountDetails(true);

    const { sortCode, accountNumber, iban, paymentToken } = formValue;

    return this.checkoutService
      .validateAccountNumberSortCode({
        sortCode,
        accountNumber,
        iban,
        paymentToken,
      })
      .pipe(
        map((isValid: boolean) => {
          if (isValid) {
            return formValue;
          }
          this.directDebitFormBuilder.setAccountDetailsErrors(this.form);
          this.gaService.tag({
            pagename: extractPageNameFromUrl(),
            event: GAEvent.GenericEvent,
            eventCategory: 'payment details form',
            eventAction: 'direct-debit/validate API fail',
            eventLabel: 'bank details invalid',
          });
          return undefined;
        }),
        finalize(() => {
          this.updateValidatingAccountDetails(false);
        })
      );
  }

  private get form(): FormGroup<DirectDebitForm> {
    return this.get(
      (state: ComponentState) => state.form
    ) as FormGroup<DirectDebitForm>;
  }
}
